import { Injectable } from '@angular/core';
import { ALLOWED_QUERY_PARAMS, MAX_REDACTED_TEXT_LENGTH, REDACTED_TEXT, SENSITIVE_PATTERNS } from './redaction-policy';

@Injectable({
  providedIn: 'root',
})
export class RedactionService {
  redactText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const text = typeof value === 'string' ? value : this.safeStringify(value);
    return this.redactSensitiveTokens(text);
  }

  redactUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      const keysToInspect: string[] = [];

      parsed.searchParams.forEach((_value, key) => {
        keysToInspect.push(key);
      });

      for (const key of keysToInspect) {
        if (!ALLOWED_QUERY_PARAMS.has(key.toLowerCase())) {
          parsed.searchParams.delete(key);
        }
      }

      return this.redactSensitiveTokens(parsed.toString());
    } catch {
      return this.redactText(url);
    }
  }

  redactObject<T>(value: T): T {
    return this.redactValue(value) as T;
  }

  private redactValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      return this.redactText(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return value;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (value instanceof URL) {
      return this.redactUrl(value.toString());
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.redactValue(item));
    }

    if (typeof value === 'object') {
      const sanitized: Record<string, unknown> = {};

      for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        sanitized[key] = this.redactValue(entry);
      }

      return sanitized;
    }

    return this.redactText(String(value));
  }

  private redactSensitiveTokens(value: string): string {
    let result = value;

    for (const pattern of SENSITIVE_PATTERNS) {
      result = result.replace(pattern, REDACTED_TEXT);
    }

    return this.truncate(result);
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value) ?? String(value);
    } catch {
      return String(value);
    }
  }

  private truncate(value: string): string {
    if (value.length <= MAX_REDACTED_TEXT_LENGTH) {
      return value;
    }

    return `${value.slice(0, MAX_REDACTED_TEXT_LENGTH)}...[truncated]`;
  }
}
